import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Alert } from './ui/alert';
import { Download, RefreshCw, CheckCircle, AlertCircle, Database } from 'lucide-react';
import { colors } from '../theme';

const ImportarFabril = () => {
  const [meses, setMeses] = useState(6);
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);

  // Carregar status da sincronização automática
  React.useEffect(() => {
    const loadSyncStatus = async () => {
      try {
        const token = localStorage.getItem('primaza_token');
        const response = await axios.get(`${API}/admin/sync-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSyncStatus(response.data);
      } catch (error) {
        console.error('Erro ao carregar status:', error);
      }
    };

    loadSyncStatus();
    // Atualizar a cada 10 segundos
    const interval = setInterval(loadSyncStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleImportar = async () => {
    setImportando(true);
    setResultado(null);
    setErro(null);

    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.post(
        `${API}/admin/importar-pedidos-fabril?meses=${meses}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResultado(response.data);
    } catch (error) {
      console.error('Erro ao importar:', error);
      setErro(error.response?.data?.detail || 'Erro ao importar pedidos do Fabril');
    } finally {
      setImportando(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-6 mb-6 rounded-lg shadow-sm">
        <div className="flex items-center gap-3">
          <Database size={32} style={{ color: colors.primaza.primary }} />
          <div>
            <h1 className="text-3xl font-bold" style={{ color: colors.primaza.secondary }}>
              Importar Pedidos do Fabril
            </h1>
            <p className="text-gray-600 mt-1">
              Sincronize pedidos do sistema Fabril (Azure) automaticamente
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Card de Status da Sincronização Automática */}
        {syncStatus && (
          <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full ${syncStatus.currently_running ? 'bg-green-500 animate-pulse' : 'bg-blue-500'}`}></div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-800">
                      {syncStatus.currently_running ? '🔄 Sincronizando...' : '✅ Sincronização Automática Ativa'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Importa automaticamente a cada <strong>{syncStatus.interval_text}</strong>
                    </p>
                  </div>
                </div>
                <RefreshCw 
                  className={`text-blue-600 ${syncStatus.currently_running ? 'animate-spin' : ''}`} 
                  size={24} 
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Card de Importação */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download size={20} style={{ color: colors.primaza.primary }} />
              Importação Manual
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Use este botão para importar manualmente. A sincronização automática já está rodando em background.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Campo de meses */}
            <div>
              <Label htmlFor="meses">Período para Importar (meses)</Label>
              <Input
                id="meses"
                type="number"
                min="1"
                max="24"
                value={meses}
                onChange={(e) => setMeses(parseInt(e.target.value))}
                className="mt-1"
              />
              <p className="text-sm text-gray-500 mt-1">
                Importará pedidos dos últimos {meses} {meses === 1 ? 'mês' : 'meses'}
              </p>
            </div>

            {/* Botão de Importar */}
            <Button
              onClick={handleImportar}
              disabled={importando}
              className="w-full"
              style={{ backgroundColor: colors.primaza.primary }}
            >
              {importando ? (
                <>
                  <RefreshCw size={18} className="mr-2 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <Download size={18} className="mr-2" />
                  Importar Pedidos do Fabril
                </>
              )}
            </Button>

            {/* Resultado da Importação */}
            {resultado && (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <div className="ml-3">
                  <h4 className="font-semibold text-green-800">Importação Concluída com Sucesso!</h4>
                  <div className="mt-2 text-sm text-green-700">
                    <p>✅ Total processados: <strong>{resultado.total_processados}</strong></p>
                    <p>✅ Novos pedidos: <strong>{resultado.novos}</strong></p>
                    <p>✅ Pedidos atualizados: <strong>{resultado.atualizados}</strong></p>
                    {resultado.erros && resultado.erros.length > 0 && (
                      <div className="mt-2">
                        <p className="font-semibold">⚠️ Erros encontrados:</p>
                        <ul className="list-disc list-inside">
                          {resultado.erros.map((erro, idx) => (
                            <li key={idx}>{erro}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </Alert>
            )}

            {/* Erro */}
            {erro && (
              <Alert className="bg-red-50 border-red-200">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <div className="ml-3">
                  <h4 className="font-semibold text-red-800">Erro na Importação</h4>
                  <p className="mt-1 text-sm text-red-700">{erro}</p>
                </div>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Card de Informações */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">ℹ️ Como Funciona</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">1.</span>
                <span>A importação conecta com a API do Fabril (Azure)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">2.</span>
                <span>Busca todos os pedidos do período selecionado</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">3.</span>
                <span>Pedidos novos são criados automaticamente</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">4.</span>
                <span>Pedidos existentes são atualizados com os novos dados</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">5.</span>
                <span>Todos os pedidos importados são marcados com origem "FABRIL"</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ImportarFabril;
